import { useState, useCallback } from 'react';

export interface UseWizardReturn<T> {
  currentStep: number;
  totalSteps: number;
  data: Partial<T>;
  isFirstStep: boolean;
  isLastStep: boolean;
  progressPercent: number;
  goNext: (stepData: Partial<T>) => void;
  goBack: () => void;
  reset: () => void;
}

export function useWizard<T extends Record<string, any>>(totalSteps: number): UseWizardReturn<T> {
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<Partial<T>>({});

  const goNext = useCallback(
    (stepData: Partial<T>) => {
      setData(prev => ({ ...prev, ...stepData }));
      setCurrentStep(prev => Math.min(prev + 1, totalSteps - 1));
    },
    [totalSteps]
  );

  const goBack = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  }, []);

  const reset = useCallback(() => {
    setCurrentStep(0);
    setData({});
  }, []);

  return {
    currentStep,
    totalSteps,
    data,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === totalSteps - 1,
    progressPercent: ((currentStep + 1) / totalSteps) * 100,
    goNext,
    goBack,
    reset,
  };
}
